/**
 * Deploy-time feature flags (#464), read once from `GET /api/me`.
 *
 * `features.domains` — the domain layer. `false` (VOYAGER_DOMAINS=off on the
 * backend) means the catalog is perspectives only: no domain picker anywhere,
 * nothing gates a perspective pick on a domain. The default is `true` so a
 * page that renders before `/api/me` answers behaves exactly as before.
 *
 * The simplified app (#1500) is the only app: `/api/me` still reports
 * `features.simple_ui: true` for older bundles, and nothing here reads it.
 */
import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import { getMe } from '../../apis';

export interface Features {
  domains: boolean;
  /** The agent builder (#1463): the setup wizard (#1622) is shown when it is on; the server always sends true (#1474), off until `/api/me` says so. */
  agent_builder: boolean;
  /** The ETL builder (#2262): the Pipelines page. */
  pipelines: boolean;
}

export const DEFAULT_FEATURES: Features = { domains: true, agent_builder: false, pipelines: false };

export interface FeaturesSlice {
  features: Features;
  /** this account may manage Voyager support staff (#472 item 3) — from /api/me, false until it answers */
  platformOwner: boolean;
  featuresLoaded: boolean;
  /** The flags, fetched once; the defaults if the fetch fails (never cached as loaded). */
  ensureFeatures: () => Promise<Features>;
  resetFeatures: () => void;
}

let inflight: Promise<Features> | null = null;
let generation = 0;

export const createFeaturesSlice: StateCreator<
  VoyagerState, [], [], FeaturesSlice
> = (set, get) => ({
  features: DEFAULT_FEATURES,
  featuresLoaded: false,
  platformOwner: false,

  resetFeatures: () => {
    generation += 1;
    inflight = null;
    set({ features: DEFAULT_FEATURES, featuresLoaded: false, platformOwner: false });
  },

  ensureFeatures: async () => {
    if (get().featuresLoaded) return get().features;
    if (!inflight) {
      const requestGeneration = generation;
      let request: Promise<Features>;
      request = getMe()
        .then((m) => {
          if (requestGeneration !== generation) return get().features;
          const { domains, agent_builder, pipelines } = { ...DEFAULT_FEATURES, ...(m.features || {}) };
          const features: Features = { domains, agent_builder: agent_builder === true, pipelines: pipelines === true };
          set({ features, featuresLoaded: true, platformOwner: m.platform_owner === true });
          return features;
        })
        .catch(() => get().features)
        .finally(() => { if (inflight === request) inflight = null; });
      inflight = request;
    }
    return inflight;
  },
});
